import React, { useState } from "react";
import {
  Button,
  Input,
  InputContainer,
  Loading,
  Modal,
} from "components/common";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { addBatchResults } from "state/reducers/emailBatching";
import Title from "components/common/Banners/common/Title";
import { useMutation } from "@apollo/react-hooks";
import { SEND_EMAILS, SEND_TEST_EMAIL } from "utils/mutations";
import MarketingNavigation from "../utils/components/MarketingNavigation";
import EmailMarketingStepper from "../utils/components/email/EmailMarketingStepper";
import MarketingToolTip from "../utils/components/MarketingToolTip";

const EmailReview = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { template, emailRecords, batchName, sender } = useSelector(
    (state) => state.emailBatching,
  );
  const {
    versionDetails: { html = "", variables = [] } = {},
    emailRecords: [firstRecord] = [],
    missingColumns,
  } = useSelector((state) => state.emailBatching);
  const [sendEmails] = useMutation(SEND_EMAILS);
  const [sendTestEmail] = useMutation(SEND_TEST_EMAIL);

  return (
    <div className="flex flex-col h-full">
      <MarketingNavigation>
        <MarketingToolTip>
          <small className="text-black-500">
            <i className="fas fa-exclamation-circle mr-2 text-gray-600" />
            <span className="text-red-500">Review and test </span>
            the campaign details before submitting.
          </small>
        </MarketingToolTip>
      </MarketingNavigation>
      <EmailMarketingStepper />
      {error && <p className="text-red-600">{error}</p>}
      <div className="flex flex-col justify-between">
        <div className="flex flex-row justify-between mb-4 pb-3">
          <Link to="/marketing/email/upload">
            <Button className="bg-red-500 outline-red-500 border-red-500 mx-1">
              Previous
            </Button>
          </Link>
          <Link to="/marketing/email/results">
            <Button
              className=""
              green
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                await sendEmails({
                  variables: {
                    data: JSON.stringify(emailRecords),
                    templateId: template.id,
                    batchName,
                    sender,
                  },
                }).then((res) => {
                  // console.dir(res.data.sendEmails);
                  const { status, message, payload } = res.data.sendEmails;
                  if (status !== "OK") {
                    console.log("error - ", message);
                    setError(message);
                  } else {
                    console.log("email sent");
                    dispatch(addBatchResults(JSON.parse(payload)));
                    history.push("/marketing/email/results");
                  }
                });
                setLoading(false);
              }}
            >
              Submit
            </Button>
          </Link>
        </div>
        <p className="font-bold text-2xl mb-4">Batch Emails</p>
        <Link to="/marketing/email-batching/review">
          <Button
            className=""
            green
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              await sendEmails({
                variables: {
                  data: JSON.stringify(emailRecords),
                  templateId: template.id,
                  batchName,
                  sender,
                },
              }).then((res) => {
                // console.dir(res.data.sendEmails);
                const { status, message, payload } = res.data.sendEmails;
                if (status !== "OK") {
                  console.log("error - ", message);
                  setError(message);
                } else {
                  console.log("email sent");
                  dispatch(addBatchResults(JSON.parse(payload)));
                  history.push("/marketing/email-batching/results");
                }
              });
              setLoading(false);
            }}
          >
            Submit
          </Button>
        </Link>
      </div>
      <div>
        {loading ? (
          <>
            <div
              className="w-full h-full absolute"
              style={{ backgroundColor: "#ffffff82" }}
            />
            <Loading />
          </>
        ) : null}
        <div className="flex mt-4">
          <div className="pr-4" style={{ maxWidth: "50%", minWidth: "30%" }}>
            <p className="font-semibold mb-2 text-lg">Review</p>
            <div>
              <div>
                <p className="font-bold">Sender</p>
                <p>{sender}</p>
              </div>
              <div>
                <p className="font-bold">Batch Name</p>
                <p>{batchName}</p>
              </div>
              <div className="mt-2">
                <p className="font-semibold">Template</p>
                <p>{`${template.name} (${template.id})`}</p>
              </div>
              <div className="mt-2">
                <p className="font-semibold">Template variables</p>
                <p>{variables.length ? variables.join(", ") : "none"}</p>
              </div>
              <div className="mt-2">
                <p className="font-semibold">Missing columns</p>
                <p>
                  {missingColumns.length ? missingColumns.join(", ") : "none"}
                </p>
              </div>
              <div className="mt-2">
                <p className="font-semibold">Number of emails to be sent</p>
                <p>{emailRecords.length}</p>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex justify-between">
              <div>Example</div>
              <Button
                paddingHorizontal={8}
                paddingVertical={4}
                style={{ fontSize: 12, paddingHorizontal: 0 }}
                onClick={() => setShowModal(true)}
              >
                Send Test
              </Button>
            </div>
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: html
                  .split("{{")
                  .map((item) => {
                    const secondSplit = item.split("}}");
                    if (secondSplit.length === 1) { return item; }
                    return secondSplit
                      .map((phrase, i) => {
                        if (i) { return phrase; }
                        return firstRecord[phrase];
                      })
                      .join("");
                  })
                  .join(""),
              }}
            />
          </div>
        </div>
      </div>
      <Modal
        isOpen={showModal}
        close={() => setShowModal(false)}
        hasCrossContainer={false}
      >
        <div className="bg-white" style={{ width: 400 }}>
          <Title
            title="Email"
            showArrow={false}
            isModal
            iconOverride="warning"
            style={{ height: "auto" }}
          />
          <div className="bg-white w-full flex-1 relative z-20 px-4 py-8">
            <div>
              <p>Enter a test email</p>
              <InputContainer className="mt-2">
                <Input
                  placeholder="me@email.com"
                  value={testEmail}
                  onChange={(e) => {
                    setTestEmail(e.target.value);
                  }}
                  type="tel"
                />
              </InputContainer>
              <Button
                className="w-full mt-8"
                onClick={async () => {
                  sendTestEmail({
                    variables: {
                      data: JSON.stringify({
                        ...firstRecord,
                        email: testEmail,
                      }),
                      templateId: template.id,
                      sender,
                    },
                  }).then((res) => {
                    // console.dir(res.data.sendEmails);
                    const { status, message } = res.data.sendTestEmail;
                    if (status !== "OK") {
                      console.log("error - ", message);
                      setError(message);
                    } else {
                      console.log("test email sent");
                    }
                  });
                  setShowModal(false);
                }}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EmailReview;
